// UAE mobile subscriber number the user types: 9 digits, always starting
// with 5 (e.g. 502305316) — the "+971" prefix is fixed/non-editable in the
// UI, so this is only ever the part after it.
export const UAE_SUBSCRIBER_REGEX = /^5\d{8}$/;

// Canonical stored/submitted User.phoneNumber form: a fixed "+971" prefix
// plus the 9-digit subscriber number. Locking every entry point (profile,
// admin create/edit user, login) to this single format is what makes the
// backend's exact-string uniqueness check and login lookup correct — see
// BPS-0176.
export const UAE_PHONE_REGEX = /^\+9715\d{8}$/;

/**
 * Recovers the 9-digit subscriber number from a phone value that may still
 * be stored in one of the formats accepted before the format was locked
 * down (+971…, 971…, or a leading 0), so an existing profile pre-fills
 * correctly into the digit-only input.
 */
export function stripUaePrefix(raw: string | null | undefined): string {
  if (!raw) return '';
  let digits = raw.replace(/\D/g, '');
  if (digits.startsWith('971')) digits = digits.slice(3);
  else if (digits.startsWith('0')) digits = digits.slice(1);
  return digits.slice(0, 9);
}

/**
 * Normalizes any legacy stored format to the canonical "+971XXXXXXXXX"
 * form (or '' if empty/unrecognized) — used when pre-filling a form so an
 * untouched field still round-trips through the canonical-only validator.
 */
export function toCanonicalUaePhone(raw: string | null | undefined): string {
  const subscriber = stripUaePrefix(raw);
  return subscriber ? `+971${subscriber}` : '';
}

/**
 * Formats a raw UAE phone string into "+971 XX XXX XXXX" (mobile)
 * or "+971 X XXX XXXX" (landline) as the user types.
 *
 * Accepts: +971…, 00971…, 0XX…, or bare digits.
 */
export function formatUaePhone(raw: string | null | undefined): string {
  if (!raw) return '';

  // Allow the user to start typing "+" naturally
  const startsWithPlus = raw.startsWith('+');
  const digits = raw.replace(/\D/g, '');

  if (!digits) return startsWithPlus ? '+' : '';

  // Early exit: still typing the country code prefix "+9" / "+97"
  if (startsWithPlus && digits.length <= 3) return `+${digits}`;

  // Normalize to start with "971"
  let d = digits;
  if (d.startsWith('00971'))
    d = d.slice(2); // 00971… → 971…
  else if (d.startsWith('0') && d.length >= 2)
    d = `971${d.slice(1)}`; // 05… → 9715…
  else if (d === '0')
    return '0'; // single "0" → keep as-is
  else if (!d.startsWith('971')) d = `971${d}`; // bare digits → prepend 971

  // Cap at 12 digits (mobile: 971 + 9 digits)
  d = d.slice(0, 12);

  if (d.length <= 3) return `+${d}`;

  const rest = d.slice(3);
  const isMobile = rest[0] === '5';

  let result = '+971';
  if (isMobile) {
    // +971 50 XXX XXXX  (groups: 2 · 3 · 4)
    if (rest.length > 0) result += ` ${rest.slice(0, 2)}`;
    if (rest.length > 2) result += ` ${rest.slice(2, 5)}`;
    if (rest.length > 5) result += ` ${rest.slice(5, 9)}`;
  } else {
    // +971 X XXX XXXX   (groups: 1 · 3 · 4)
    if (rest.length > 0) result += ` ${rest.slice(0, 1)}`;
    if (rest.length > 1) result += ` ${rest.slice(1, 4)}`;
    if (rest.length > 4) result += ` ${rest.slice(4, 8)}`;
  }

  return result;
}
